import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ImpersonationBanner from './ImpersonationBanner';
import {
  UsersIcon,
  ArrowLeftIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';

/*
 * Deliberately distinct from the admin Layout — a councillor should never
 * mistake their portal for the full municipal admin dashboard. Mirrors the
 * platform console's approach (see pages/platform/PlatformLayout.tsx): a
 * single top bar, no sidebar, no command palette, one page.
 */

const CouncillorLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Councillor Portal · uMngeniFix';
    return () => { document.title = previous; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ImpersonationBanner />
      <header className="border-b border-teal-800 bg-teal-900">
        <div className="mx-auto flex h-16 max-w-7xl flex-wrap items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            {user?.role !== 'councillor' && (
              <Link
                to="/live-dashboard"
                className="flex items-center gap-1 rounded px-2 py-1 text-sm text-teal-300 hover:bg-teal-800 hover:text-white"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Admin dashboard
              </Link>
            )}
            <div className="flex items-center gap-2">
              <UsersIcon className="h-5 w-5 text-teal-400" />
              <span className="text-sm font-semibold tracking-wide text-white">COUNCILLOR PORTAL</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={toggleTheme}
              className="rounded p-2 text-teal-300 hover:bg-teal-800 hover:text-white"
            >
              {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>
            <span className="mx-1 hidden text-sm text-teal-300 sm:inline">{user?.firstName} {user?.lastName}</span>
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm text-teal-300 hover:bg-teal-800 hover:text-white"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              Edit profile
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm text-teal-300 hover:bg-teal-800 hover:text-white"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 text-gray-900 dark:text-gray-100 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
};

export default CouncillorLayout;
